import { Ionicons } from '@expo/vector-icons';
import { useRouter } from 'expo-router';
import React from 'react';
import { Pressable, View } from 'react-native';
import { useTranslation } from 'react-i18next';

import { AppText } from '@/components/ui/AppText';
import { useThemePalette } from '@/config/theme';
import { useIsFavorite, useToggleFavorite } from '@/features/favorites/hooks';
import { useAuth } from '@/lib/auth/AuthProvider';
import type { FavoriteEntityType } from '@/lib/supabase/types';

interface FollowButtonProps {
  entityType: FavoriteEntityType;
  entityId: string | undefined;
  /** 'icon' = circular star (compact), 'pill' = star + label CTA. */
  variant?: 'icon' | 'pill';
}

/**
 * Follow / unfollow any entity (team, competition, player). The active star is
 * VOLT - not captain gold, which the design system reserves for the C badge.
 * Guests are routed to sign-in on tap rather than failing silently.
 */
export function FollowButton({ entityType, entityId, variant = 'icon' }: FollowButtonProps) {
  const palette = useThemePalette();
  const router = useRouter();
  const { t } = useTranslation();
  const { userId } = useAuth();
  const isFavorite = useIsFavorite(entityType, entityId);
  const toggle = useToggleFavorite();

  const onPress = () => {
    if (!entityId) return;
    if (!userId) {
      router.push('/auth/sign-in');
      return;
    }
    toggle.mutate({ entityType, entityId, next: !isFavorite });
  };

  const label = isFavorite ? t('favorites.following') : t('favorites.follow');
  const iconName = isFavorite ? 'star' : 'star-outline';
  const iconColor = isFavorite ? palette.onVolt : palette.soft;

  if (variant === 'pill') {
    return (
      <Pressable
        accessibilityRole="button"
        accessibilityLabel={label}
        accessibilityState={{ selected: isFavorite }}
        onPress={onPress}
        className={`flex-row items-center gap-1.5 self-start rounded-full px-3.5 py-2 active:opacity-80 ${
          isFavorite ? 'bg-volt' : 'border border-volt-line bg-volt/10'
        }`}
      >
        <Ionicons
          name={iconName}
          size={14}
          color={isFavorite ? palette.onVolt : palette.voltBright}
        />
        <AppText
          variant="micro"
          weight="bold"
          style={{ color: isFavorite ? palette.onVolt : palette.voltBright }}
        >
          {label}
        </AppText>
      </Pressable>
    );
  }

  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={label}
      accessibilityState={{ selected: isFavorite }}
      onPress={onPress}
      hitSlop={8}
      className="h-9 w-9 items-center justify-center rounded-full active:opacity-70"
    >
      <View
        className={`h-9 w-9 items-center justify-center rounded-full ${
          isFavorite ? 'bg-volt' : 'border border-line bg-surface'
        }`}
      >
        <Ionicons name={iconName} size={17} color={iconColor} />
      </View>
    </Pressable>
  );
}
